// src/components/store/NewItemsComponents/TotalBar.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@radix-ui/themes";
import { Loader } from "lucide-react";
import { formatPrice } from "../../../scripts/generalScript";
import { useSalesContext } from "../../../contexts/SalesContext";

export const TotalBar = ({
  // Props básicas
  currentStepIndex,
  totalSteps,
  handleNext,
  isButtonDisabled,
  getButtonText,

  // Props de carga
  isSubmitting,
  isUpdatingStock,
  isCoordsLoading,
  termsAccepted = false,

  // Props de totales
  newOrderTotal,
  totals, // Para NewRefills
  displayTotals, // Para NewRefills con totales actualizados

  // Props de progreso
  progress,

  // Props adicionales para NewRefills
  showDeliveryBreakdown = false,
  isRefillMode = false,
  customLabel = null,
}) => {
  // Obtener el estado de transición del contexto
  const { isChangingAddress } = useSalesContext();

  // Determinar qué total mostrar (compatible con ambos componentes)
  const totalAmount =
    displayTotals?.total || totals?.total || newOrderTotal || 0;
  const subtotalAmount = displayTotals?.subtotal || totals?.subtotal || 0;
  const deliveryCost = displayTotals?.deliveryCost || totals?.deliveryCost || 0;

  // Calcular el progreso visual
  const visualProgress = (() => {
    if (progress === 100 || progress === 1 || progress === 2) {
      return 100;
    }

    // Si estamos en el último paso, el progreso depende de los términos
    if (currentStepIndex === totalSteps - 1) {
      // Si los términos están aceptados, 100%, si no, un poco menos
      return termsAccepted ? 100 : ((totalSteps - 0.5) / totalSteps) * 100;
    }

    const stepsCompleted = currentStepIndex + 1;
    const progressPercentage = Math.min(
      100,
      Math.max(0, (stepsCompleted / totalSteps) * 100)
    );
    return progressPercentage;
  })();
  
  // Manejar el click del botón
  const handleAction = (event) => {
    event.preventDefault();
    if (isButtonDisabled || isSubmitting) {
      console.log(
        "🔴 TotalBar: Click ignorado - botón deshabilitado o enviando"
      );
      return;
    }
    console.log("✅ TotalBar: Llamando handleNext");
    handleNext();
  };

  // Determinar si está cargando
  const isLoading = isSubmitting || isUpdatingStock || isCoordsLoading;

  // Obtener el texto del botón (compatible con ambos formatos)
  const buttonText =
    typeof getButtonText === "function" ? getButtonText() : getButtonText;

  // Determinar la etiqueta del total
  const totalLabel =
    customLabel ||
    (currentStepIndex === totalSteps - 1 ? "Total a pagar" : "Subtotal");

  // ✅ IMPORTANTE: Combinar todas las condiciones para deshabilitar el botón
  const shouldDisableButton =
    isButtonDisabled || isSubmitting || isChangingAddress;

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      exit={{ y: 100 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50"
    >
      {/* Barra de progreso */}
      {progress !== 100 && (
        <div className="w-full h-1 bg-gray-100">
          <motion.div
            className={`h-full bg-blue-600`}
            initial={{ width: 0 }}
            animate={{ width: `${visualProgress}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 xl:px-0 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Información del total */}
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-gray-600">{totalLabel}</p>

                {/* Mostrar loader cuando está cambiando de dirección */}
                {isChangingAddress ? (
                  <div className="flex items-center gap-2 h-[32px]">
                    <Loader className="animate-spin text-gray-500" size={20} />
                    <span className="text-lg text-gray-500">
                      Actualizando...
                    </span>
                  </div>
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={totalAmount}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="text-2xl font-medium text-gray-900"
                    >
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format(totalAmount)}
                    </motion.p>
                  </AnimatePresence>
                )}
              </div>
            </div>
          </div>

          {/* Botón de acción */}
          <motion.div
            whileHover={{ scale: shouldDisableButton ? 1 : 1.03 }}
            whileTap={{ scale: shouldDisableButton ? 1 : 0.98 }}
          >
            <button
              onClick={handleAction}
              disabled={shouldDisableButton}
              className={`px-8 py-3 rounded-lg font-medium transition-colors text-base flex items-center justify-center min-w-[160px] h-[48px] ${
                shouldDisableButton
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>
                    {isSubmitting
                      ? "Procesando..."
                      : isRefillMode
                      ? "Procesando..."
                      : "Cargando..."}
                  </span>
                </div>
              ) : (
                buttonText
              )}
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default TotalBar;
